import '@iotize/tap/service/impl/variable';
import '@iotize/tap/service/impl/target';
import '@iotize/tap/service/impl/interface';

import { ProgressState } from '@iotize/common/progress/api';

import { Tap } from '@iotize/tap';
import { VariableType, VariableMeta } from '@iotize/tap/service/impl/variable';
import { Observer } from 'rxjs';

import {
  createTapBundleFromConfig,
  TapBundleConfig,
  TLV_BUNDLE_CONVERTER,
  TapVariableConfig,
} from '../target-variable/tap-bundle/tap-bundle';
import { KeyTypeType } from '../utility/editable-data-stream';
import { TypedDataManager } from './data-manager';

export interface TapDataManagerConfig<DataType> {
  bundles: BundlesConfig<DataType>;
}

export type BundlesConfig<DataType> = Record<
  keyof DataType,
  TapBundleConfig<keyof DataType[keyof DataType]>
>;

export function createTapBundlesFromConfig<DataType>(
  tap: Tap,
  bundles: BundlesConfig<DataType>,
) {
  const bundleDataStreams: any = {};

  for (const name in bundles) {
    bundleDataStreams[name] = createTapBundleFromConfig(
      tap,
      name,
      bundles[name],
    );
  }
  return bundleDataStreams;
}

export function createDataManagerFromTapConfig<DataType>(
  tap: Tap,
  config: TapDataManagerConfig<DataType>,
): TypedDataManager<DataType> {
  const { bundles } = config;

  return new TypedDataManager<any>(
    tap,
    createTapBundlesFromConfig<DataType>(tap, bundles),
  );
}

export namespace DataManagerSync {
  export type Event = { progress: ProgressState } & (
    | {
        step: 'start-read-variable-config';
        profileData: Record<string, Uint8Array>;
      }
    | {
        step: 'read-variable';
        variableId: number;
      }
    | {
        step: 'end-read-variable-config';
      }
  );
}

export async function readDataManagerTapConfigFromTap<
  ExpectedDataType extends Record<
    KeyTypeType,
    TapBundleConfig<KeyTypeType>
  > = Record<KeyTypeType, TapBundleConfig<KeyTypeType>>,
>(
  tap: Tap,
  config: {
    bundleIdNameMap?: Record<number, string>;
    variableIdNameMap?: Record<number, string>;
    observer?: Observer<DataManagerSync.Event>;
  } = {
    bundleIdNameMap: {},
    variableIdNameMap: {},
  },
) {
  const { bundleIdNameMap, variableIdNameMap, observer } = config;

  (await tap.service.target.connect()).successful();
  const readProfileResponse = await tap.service.variable.readProfile();
  // if (!readProfileResponse.isSuccessful()) {
  //     switch (readProfileResponse.status) {
  //         case ResultCode.SERVICE_UNAVAILABLE:
  //             // If service is unavailable, it probably means that target is not connected yet
  //             (await tap.service.target.connect()).successful();
  //             readProfileResponse = await tap.service.variable.readProfile();
  //             break;
  //         default:
  //             // Throw error otherwise
  //             readProfileResponse.successful();
  //     }
  // }
  readProfileResponse.successful();
  const rawBundleData = readProfileResponse.rawBody();
  const profileData = TLV_BUNDLE_CONVERTER.decode(rawBundleData);

  const bundles: Record<KeyTypeType, TapBundleConfig<KeyTypeType>> = {};
  const variableCount = Object.keys(profileData).length;
  let index = 0;

  observer?.next({
    step: 'start-read-variable-config',
    profileData,
    progress: {
      loaded: index,
      total: variableCount,
    },
  });

  for (const variableId in profileData) {
    const variableIdInt = parseInt(variableId, 10);
    if (isNaN(variableIdInt)) {
      throw new Error(`Invalid variable integer identifier: ${variableId}`);
      continue;
    }
    observer?.next({
      step: 'read-variable',
      variableId: variableIdInt,
      progress: {
        loaded: index + 1,
        total: variableCount,
      },
    });

    const {
      bundleId,
      variableName,
      config: variableInfo,
    } = await readTapVariableConfigFromTap(
      tap,
      variableIdInt,
      variableIdNameMap ? variableIdNameMap[variableIdInt] : undefined,
    );
    let bundleName: string | number;
    if (bundleIdNameMap && bundleId in bundleIdNameMap) {
      bundleName = bundleIdNameMap[bundleId];
    } else {
      let readBundleName = (await tap.service.bundle.getName(bundleId)).body();
      // Fix bug on firmware versions that returns fixed size for bundle name (at least until firmware v1.106)
      const endOfStringIndex = readBundleName.indexOf('\u0000');
      if (endOfStringIndex >= 0) {
        readBundleName = readBundleName.substr(0, endOfStringIndex);
      }
      if (!readBundleName) {
        bundleName = bundleId;
      } else {
        bundleName = readBundleName;
      }
    }
    if (!(bundleName in bundles)) {
      bundles[bundleName] = {
        id: bundleId,
        variables: {},
      };
    } else {
      // TODO what to do when duplicated bundle name ?
    }
    bundles[bundleName].variables[variableName] = variableInfo;
    index++;
  }

  observer?.next({
    step: 'end-read-variable-config',
    progress: {
      loaded: variableCount,
      total: variableCount,
    },
  });

  return bundles as ExpectedDataType;
}

export async function readTapVariableConfigFromTap<ExpectedType = any>(
  tap: Tap,
  variableId: number,
  variableName?: string,
): Promise<{
  bundleId: number;
  variableName: string;
  config: TapVariableConfig<ExpectedType>;
}> {
  const [typeResponse, bundleIdResponse, lengthResponse, metaResponse] =
    await tap.service.interface.executeMultipleCalls([
      tap.service.variable.getTypeCall(variableId),
      tap.service.variable.getBundleIdCall(variableId),
      tap.service.variable.getNumberOfElementsCall(variableId),
      tap.service.variable.getMetaCall(variableId),
    ]);

  const bundleId: number = bundleIdResponse.body();
  const variableType: VariableType = typeResponse.body();
  const length: number = lengthResponse.body();
  const dataType = variableType.data;

  if (!variableName) {
    const meta: VariableMeta = metaResponse.body();
    variableName = meta.name || variableId.toString();
  }
  return {
    bundleId,
    variableName,
    config: {
      id: variableId,
      dataType,
      length,
    },
  };
}
